import React, { useRef, useState } from 'react';
import clsx from 'clsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { formatNumber } from '@utils/utils';
import { useDetectClickOutside } from '@hooks/useDetectClickOutside/useDetectClickOutside';
import SvgAngleLeft from '@icons/angle-left.svg?react';
import SvgAngleRight from '@icons/angle-right.svg?react';
import SvgChevron from '@icons/chevron.svg?react';
import RingilPaginator from '../RingilPaginator';
import { buildPaginationItems, clamp, PaginationItem } from '../paginationItems';

interface ComponentProps {
  totalRecords: number;
  /** Current page, 1-based (as returned by the API `page` field). */
  page: number;
  pageSize: number;
  rowsPerPageOptions: number[];
  /** Row count at which the API stops counting; at or above it the total renders as "10 000+". */
  totalCapAt?: number;
  /** PrimeReact paginator event shape, as in v2: `page` is 0-based, `rows` is the page size. */
  onPage: (event: { page: number; rows: number }) => void;
  isMobile: boolean;
}

// 26px squares with no frame; only the current page is outlined (Figma 21940:61155).
const itemBase =
  'inline-flex items-center justify-center h-[26px] min-w-[26px] px-1 rounded border border-solid font-[inherit] text-[11.5px] leading-none select-none transition-colors duration-200';

// Frame of every item except the current page; kept out of itemBase so it never competes with the
// active border.
const idle = 'bg-transparent border-transparent';

const PageSizeDropdown: React.FC<{ value: number; options: number[]; onChange: (size: number) => void }> = ({ value, options, onChange }) => {
  const { tr } = useTranslationLgs();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useDetectClickOutside([ref], () => setOpen(false));

  const label = (size: number) => `${size} ${tr('RingilPaginator.items', 'items')}`;

  return (
    <div className="flex items-center gap-3 text-[11.5px]">
      <span className="text-content-secondary whitespace-nowrap">{tr('RingilPaginator.show', 'Show')}</span>
      <div ref={ref} className="relative">
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen(o => !o)}
          className="group flex items-center gap-3 p-0 bg-transparent border-0 cursor-pointer font-[inherit] text-[length:inherit]"
        >
          <span className="text-content-primary whitespace-nowrap group-hover:text-complementary-brandDark">{label(value)}</span>
          <SvgChevron
            className={clsx('w-3 shrink-0 text-content-brand transition-transform duration-200 group-hover:text-complementary-brandDark', { 'rotate-180': open })}
          />
        </button>
        {open && (
          <div
            role="listbox"
            className="absolute right-0 bottom-full mb-1 z-20 bg-base-secondary rounded shadow-md border border-solid border-content-quaternary overflow-hidden min-w-full"
          >
            {options.map(opt => (
              <div
                key={opt}
                role="option"
                aria-selected={opt === value}
                onClick={() => {
                  onChange(opt);
                  setOpen(false);
                }}
                className={clsx(
                  'px-3 py-2 cursor-pointer hover:bg-base-tertiary whitespace-nowrap',
                  opt === value ? 'text-content-brand font-medium' : 'text-content-secondary',
                )}
              >
                {label(opt)}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Compact paginator of RingilDataTable v3. Same contract and page-range rules as {@link RingilPaginator};
 * on mobile it simply renders that one, since the compact look is a desktop table look.
 */
const PaginatorV3: React.FC<ComponentProps> = props => {
  const { totalRecords, page, pageSize, rowsPerPageOptions, totalCapAt, onPage, isMobile } = props;
  const { tr } = useTranslationLgs();
  // Clicking an ellipsis turns it into a "jump to page" input, as in v2.
  const [editingEllipsis, setEditingEllipsis] = useState<PaginationItem | null>(null);
  const [ellipsisValue, setEllipsisValue] = useState('');

  if (isMobile) return <RingilPaginator {...props} />;

  const totalPages = Math.max(1, Math.ceil((totalRecords || 0) / pageSize));
  const current = clamp(page || 1, 1, totalPages);
  const capped = totalCapAt != null && (totalRecords || 0) >= totalCapAt;

  const goTo = (target: number) => {
    const next = clamp(target, 1, totalPages);
    if (next !== current) onPage({ page: next - 1, rows: pageSize });
  };

  const commitEllipsis = () => {
    const parsed = parseInt(ellipsisValue, 10);
    setEditingEllipsis(null);
    setEllipsisValue('');
    if (!Number.isNaN(parsed)) goTo(parsed);
  };

  const arrow = (icon: React.ReactNode, target: number, disabled: boolean, label: string) => (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={() => goTo(target)}
      className={clsx(itemBase, idle, disabled ? 'text-content-tertiary cursor-not-allowed' : 'text-content-brand cursor-pointer hover:text-complementary-brandDark')}
    >
      {icon}
    </button>
  );

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 w-full p-5 font-['DM_Sans']">
      <div className="flex items-center gap-[6px]">
        {arrow(<SvgAngleLeft className="w-[14px] h-[14px]" />, current - 1, current <= 1, tr('generic.previous', 'Previous'))}
        {buildPaginationItems(current, totalPages).map((item, idx) => {
          if (item === 'start-ellipsis' || item === 'end-ellipsis') {
            return editingEllipsis === item ? (
              <input
                key={`${item}-${idx}`}
                autoFocus
                value={ellipsisValue}
                inputMode="numeric"
                placeholder="…"
                onChange={e => setEllipsisValue(e.target.value.replace(/[^0-9]/g, ''))}
                onKeyDown={e => (e.key === 'Enter' ? commitEllipsis() : e.key === 'Escape' ? setEditingEllipsis(null) : undefined)}
                onBlur={commitEllipsis}
                aria-label={tr('RingilPaginator.goToPage', 'Go to page')}
                className="h-[26px] w-[44px] text-center bg-base-secondary border border-solid border-content-brand rounded text-[11.5px] text-content-primary outline-none"
              />
            ) : (
              <button
                key={`${item}-${idx}`}
                type="button"
                aria-label={tr('RingilPaginator.goToPage', 'Go to page')}
                onClick={() => {
                  setEllipsisValue('');
                  setEditingEllipsis(item);
                }}
                className={clsx(itemBase, idle, 'text-content-brand cursor-pointer hover:text-complementary-brandDark')}
              >
                …
              </button>
            );
          }
          const active = item === current;
          return (
            <button
              key={item}
              type="button"
              aria-current={active ? 'page' : undefined}
              onClick={() => goTo(item)}
              className={clsx(
                itemBase,
                active
                  ? 'bg-base-secondary border-content-brand text-content-primary'
                  : clsx(idle, 'text-content-brand cursor-pointer hover:text-complementary-brandDark'),
              )}
            >
              {formatNumber(item, 0)}
            </button>
          );
        })}
        {arrow(<SvgAngleRight className="w-[14px] h-[14px]" />, current + 1, current >= totalPages, tr('generic.next', 'Next'))}
        <span className="ml-1 text-[11.5px] whitespace-nowrap">
          <span className="font-semibold text-content-primary">
            {formatNumber(capped ? totalCapAt : totalRecords || 0, 0)}
            {capped && '+'}
          </span>{' '}
          <span className="text-content-secondary">{tr('RingilPaginator.results', 'results')}</span>
        </span>
      </div>
      <PageSizeDropdown value={pageSize} options={rowsPerPageOptions} onChange={size => onPage({ page: 0, rows: size })} />
    </div>
  );
};

export default PaginatorV3;
